import React, { useState, useEffect } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  SwapHoriz as SwitchIcon,
  Check as CheckIcon,
  Groups as FamilyIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { multiFamilyService } from '../services/multiFamilyService';
import logger from '../utils/logger';

/**
 * Universal Family Switcher UI (B.2)
 * Displays in the navigation bar when a user belongs to multiple families.
 * Allows quick switching between families without logging out.
 */
const FamilySwitcher = ({ onSwitch }) => {
  const { currentUser, refreshUserData } = useAuth();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const loadFamilies = async () => {
      if (!currentUser?.uid) {
        setFamilies([]);
        setLoading(false);
        return;
      }

      try {
        // Ensure multi-family migration for legacy users
        await multiFamilyService.migrateToMultiFamily(currentUser.uid);
        const userFamilies = await multiFamilyService.getUserFamilies(currentUser.uid);
        setFamilies(userFamilies);
      } catch (err) {
        logger.error('Error loading families:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFamilies();
  }, [currentUser?.uid, currentUser?.activeFamilyCode, currentUser?.familyCode]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchFamily = async (familyCode) => {
    const activeFamilyCode = currentUser?.activeFamilyCode || currentUser?.currentFamily || currentUser?.familyCode;
    if (familyCode === activeFamilyCode) {
      handleClose();
      return;
    }

    setSwitching(true);
    try {
      await multiFamilyService.switchFamily(currentUser.uid, familyCode);
      await refreshUserData({ waitForField: 'activeFamilyCode' });
      handleClose();
      if (onSwitch) onSwitch(familyCode);
    } catch (err) {
      logger.error('Error switching family:', err);
    } finally {
      setSwitching(false);
    }
  };

  // Don't render if user only belongs to one family
  if (loading || families.length <= 1) return null;

  const currentFamilyCode = currentUser?.activeFamilyCode || currentUser?.currentFamily || currentUser?.familyCode;

  return (
    <>
      <Tooltip title="Switch Family">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{ ml: 1 }}
          aria-label="Switch family"
          aria-controls={open ? 'family-switcher-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge
            badgeContent={families.length}
            color="primary"
            max={9}
          >
            <SwitchIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id="family-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 280, maxWidth: 360 }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Switch Family
          </Typography>
        </Box>
        <Divider />
        {families.map((family) => {
          const isCurrent = family.familyCode === currentFamilyCode;
          return (
            <MenuItem
              key={family.familyCode}
              onClick={() => handleSwitchFamily(family.familyCode)}
              selected={isCurrent}
              disabled={switching}
            >
              <ListItemIcon>
                {isCurrent ? (
                  <CheckIcon color="primary" />
                ) : (
                  <FamilyIcon />
                )}
              </ListItemIcon>
              <ListItemText
                primary={family.name}
                secondary={
                  <Box component="span" sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                    <Chip
                      label={family.role}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {family.memberCount} member{family.memberCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                }
              />
              {switching && isCurrent && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default FamilySwitcher;
